import React from "react";
import styled from "styled-components";
import RevealOnScroll from "../../utils/RevealOnScroll";
import WorkButtons from "../Header/WorkButtons";
import { SiJavascript, SiReact, SiHtml5, SiCss3, SiPython, SiOpenai } from "react-icons/si";
import { motion } from "framer-motion";
import TextShift from "../../utils/TextShift";
import { Link } from "react-router-dom";
import { colors } from "../../utils/theme";

const DevelopContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  color: white;
  background: transparent;
  padding: 0 1rem 4rem;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.5rem 3rem;
  }
`;

const TitleContainer = styled.div`
  margin-top: 6rem;
  margin-bottom: 0;
  opacity: 0.9;
  width: 100%;
  display: flex;
  justify-content: center;
  @media (max-width: 768px) {
    margin-top: 1rem;
    padding-bottom: 1.3rem;
  }
`;

const NavigationContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  margin-top: -2rem;
`;

const SubtextContainer = styled.div`
  width: 100%;
  max-width: 800px;
  text-align: left;
  padding: 2rem;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0.5rem;
    max-width: 100%;
  }
`;

const Subtext = styled.p`
  font-family: "maitree", serif;
  font-size: 1.4rem;
  line-height: 1.6;
  color: #9f9f9f;
  font-weight: 300;
  max-width: 600px;
  margin: 0;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    width: 350px;
    padding: 0;
  }
`;

const TechStackContainer = styled.div`
  width: 100%;
  max-width: 800px;
  padding: 2rem 2rem 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6.4rem;
  margin: 0 auto;

  @media (max-width: 768px) {
   padding: 1rem 0rem 0;
    gap: 2rem;
    justify-content: center;
    flex-wrap: wrap;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    gap: 3rem;
    padding: 1rem 0 0;
  }
`;

const IconWrapper = styled(motion.div)`
  color: #9f9f9f;
  font-size: 2.4rem;
  opacity: 0.7;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }

  &:hover {
    color: white;
    opacity: 1;
    transform: translateY(-2px);
  }
`;

const iconVariants = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
};

const ProjectsContainer = styled.div`
  width: 100%;
  max-width: 900px;
  padding: 4rem 2rem 6rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 0 auto;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 4rem 1.5rem 5rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 350px;
    padding: 2rem 0 4rem;
    gap: 1.5rem;
  }
`;

const ProjectBox = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0;
  background: rgba(50, 50, 50, 0.5);
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  text-decoration: none;
  color: white;
  position: relative;
  
  width: 100%;
  transition: all 0.3s ease-in-out;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    height: 350px;
  }

  @media (max-width: 768px) {
    height: 300px;
    width: 100%;
  }

  @media (max-width: 480px) {
    height: 250px;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(50, 50, 50, 0.7);
  }
`;

const ProjectInfo = styled.div`
  position: relative;
  z-index: 2;
  padding: 2rem;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProjectTitle = styled.h3`
  font-size: 2rem;
  margin: 0;
  font-weight: 500;
  color: ${colors.titleColor};
  font-family: "moret-variable", serif;
`;

const ProjectDescription = styled.p`
  font-size: 1.1rem;
  color: ${colors.subtitleColor};
  line-height: 1.6;
  margin: 1rem 0;
  font-family: "maitree", serif;
`;

const Tag = styled.span`
  font-size: 1rem;
  color: ${colors.subtitleColor};
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const TagContainer = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-top: 0.6rem;
`;

function Develop() {
  const featuredProjects = [
    {
      title: "AutoMail",
      description: "Leveraging AI to optimize email workflows.",
      tags: ["Python", "AI", "APIs", "Gcloud"],
      link: "/automail"
    },
    {
      title: "Macker",
      description: "Webscraper and email automation for manga updates.",
      tags: ["Python", "Gcloud", "BeautifulSoup"],
      link: "/macker"
    },
    {
      title: "Rhys Bot",
      description: "Intelligent chatbot to help manage club operations.",
      tags: ["JavaScript", "Node.js", "APIs"],
      link: "/rhys-bot"
    },
    {
      title: "XML Tools",
      description: "Suite of tools for parsing, validating, and transforming XML documents.",
      tags: ["XML", "Python", "YAML"],
      link: "/xml-tools"
    },
    {
      title: "SocialNest",
      description: "A website to explore and visualize social connections.",
      tags: ["UI/UX", "Full-Stack", "Figma"],
      link: "/SocialNest"
    }
  ];

  return (
    <DevelopContainer>
      <RevealOnScroll delay={0}>
        <TitleContainer>
          <TextShift
            text="Develop"
            fontSize="10rem"
            mobileFontSize="5rem"
            fontFamily="moret-variable, serif"
            maxWeight={900}
            minWeight={200}
            adjacentWeightChange={100}/>
        </TitleContainer>
      </RevealOnScroll>

      <RevealOnScroll delay={200}>
        <NavigationContainer>
          <WorkButtons excludeButton="develop" />
        </NavigationContainer>
      </RevealOnScroll>

      <RevealOnScroll delay={300}>
        <SubtextContainer>
          <Subtext>
          I develop solutions that leverage AI systems to automate tasks while also crafting engaging, user-focused front-end experiences, blending efficiency with seamless design.          </Subtext>
        </SubtextContainer>
      </RevealOnScroll>

      <RevealOnScroll delay={400}>
        <TechStackContainer>
          {[
            { Icon: SiJavascript, delay: 0 },
            { Icon: SiReact, delay: 100 },
            { Icon: SiHtml5, delay: 200 },
            { Icon: SiCss3, delay: 300 },
            { Icon: SiPython, delay: 400 },
            { Icon: SiOpenai, delay: 500 }
          ].map(({ Icon, delay }, index) => (
            <IconWrapper
              key={index}
              variants={iconVariants}
              initial="initial"
              animate="animate"
              transition={{ duration: 0.5, delay: delay / 1000 }}
            >
              <Icon />
            </IconWrapper>
          ))}
        </TechStackContainer>
      </RevealOnScroll>

      <ProjectsContainer>
        {featuredProjects.map((project, index) => (
          <RevealOnScroll delay={350} key={index}>
            <ProjectBox to={project.link}>
              <ProjectInfo>
                <div>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDescription>
                    {project.description}
                  </ProjectDescription>
                  <TagContainer>
                    {project.tags.map((tag, index) => (
                      <React.Fragment key={index}>
                        <Tag>{tag}</Tag>
                        {index < project.tags.length - 1 && <Tag>•</Tag>}
                      </React.Fragment>
                    ))}
                  </TagContainer>
                </div>
              </ProjectInfo>
            </ProjectBox>
          </RevealOnScroll>
        ))}
      </ProjectsContainer>
    </DevelopContainer>
  );
}

export default Develop;
